import type { ScoreRow } from '../lib/api.ts'

/**
 * The label and the reading, field by field.
 *
 * The score says how far apart they are; this says where. Going from "71%" to
 * "the form and the volume were dropped" otherwise means querying Postgres,
 * which is not a thing the person correcting a label can do.
 *
 * **The rows come from the scorer.** Nothing here re-derives a comparison: a
 * screen that paired ingredients its own way would show a field as matching
 * that the number counted wrong, and two answers that disagree are worse than
 * one number with no explanation.
 *
 * The table on its own, so the two places that show it -- the diff a
 * simulation opens, and the run a case screen asks for -- cannot drift into
 * two tables that read differently about the same thing.
 */

const TONE: Readonly<Record<string, string>> = {
  match: 'text-text-weak',
  differ: 'text-text-error',
  missed: 'text-text-error',
  invented: 'text-text-error',
  'both-absent': 'text-text-weak',
}

const VERDICT: Readonly<Record<string, string>> = {
  match: 'confere',
  differ: 'diverge',
  missed: 'não leu',
  invented: 'inventou',
  'both-absent': 'ausente nos dois',
}

export function ScoreRows({ rows }: { rows: readonly ScoreRow[] }) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          {['Campo', 'Gabarito', 'Leitura', ''].map((h, i) => (
            <th
              key={h + String(i)}
              scope="col"
              className="border-stroke-weak text-tTiny text-text-weak border-b px-2 py-2 text-left font-semibold uppercase"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const wrong = row.match !== 'match' && row.match !== 'both-absent'
          return (
            <tr key={i} className={wrong ? 'bg-fill-error-weak/20' : undefined}>
              <td className="border-stroke-weak text-tSmall text-text-weak border-b px-2 py-1.5 whitespace-nowrap">
                {row.preparation === undefined
                  ? row.label
                  : `P${row.preparation + 1} · ${row.label}`}
              </td>
              {/* Both sides shown even when one is empty: a dash on the
                  right is "did not read it", on the left "was not there",
                  and those are different mistakes. */}
              <td className="border-stroke-weak text-tP text-text-strong border-b px-2 py-1.5">
                {row.expected ?? '—'}
              </td>
              <td
                className={`border-stroke-weak text-tP border-b px-2 py-1.5 ${
                  wrong ? 'text-text-error font-semibold' : 'text-text-strong'
                }`}
              >
                {row.actual ?? '—'}
              </td>
              <td
                className={`border-stroke-weak text-tTiny border-b px-2 py-1.5 whitespace-nowrap ${TONE[row.match] ?? 'text-text-weak'}`}
              >
                {VERDICT[row.match] ?? row.match}
                {/* A row that did not count towards the number should not
                    look like one that did and passed. */}
                {!row.counted && ' · não conta'}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
